import React from 'react';

// Array warna gradient yang menarik
const colorGradients = [
  ['#667eea', '#764ba2'], // Purple
  ['#f093fb', '#f5576c'], // Pink-Red
  ['#4facfe', '#00f2fe'], // Blue-Cyan
  ['#43e97b', '#38f9d7'], // Green-Cyan
  ['#fa709a', '#fee140'], // Pink-Yellow
  ['#30cfd0', '#330867'], // Cyan-Purple
  ['#a8edea', '#fed6e3'], // Light Cyan-Pink
  ['#ff9a9e', '#fecfef'], // Pink
  ['#ffecd2', '#fcb69f'], // Peach
  ['#ff8a80', '#ea6100'], // Orange-Red
  ['#84fab0', '#8fd3f4'], // Green-Blue
  ['#a1c4fd', '#c2e9fb'], // Light Blue
];

/**
 * Generate a deterministic color gradient based on user ID or name
 */
function getColorForUser(identifier: string | number): string[] {
  // Convert identifier to number for consistent color selection
  let hash = 0;
  const str = String(identifier);
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colorGradients.length;
  return colorGradients[index];
}

/**
 * Get initials from name
 */
function getInitials(name: string): string {
  if (!name) return 'U';
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Generate default avatar SVG as data URL
 */
export function getDefaultAvatar(name?: string, id?: number): string {
  const identifier = id !== undefined ? id : (name || 'user');
  const colors = getColorForUser(identifier);
  const initials = getInitials(name || 'User');
  
  // Use encodeURIComponent instead of base64 for better compatibility
  const svg = `<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
<defs>
<linearGradient id="grad-${identifier}" x1="0%" y1="0%" x2="100%" y2="100%">
<stop offset="0%" style="stop-color:${colors[0]};stop-opacity:1" />
<stop offset="100%" style="stop-color:${colors[1]};stop-opacity:1" />
</linearGradient>
</defs>
<circle cx="50" cy="50" r="50" fill="url(#grad-${identifier})"/>
<text x="50" y="50" font-family="Arial, sans-serif" font-size="36" font-weight="600" fill="white" text-anchor="middle" dominant-baseline="central">${initials}</text>
</svg>`;
  
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * Generate default company logo SVG as data URL
 */
export function getDefaultCompanyLogo(name?: string, id?: number): string {
  const identifier = id !== undefined ? id : (name || 'company');
  const colors = getColorForUser(identifier);
  // Get first 2 letters of company name
  const initials = name ? name.substring(0, 2).toUpperCase() : 'CO';
  
  const svg = `<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
<defs>
<linearGradient id="grad-company-${identifier}" x1="0%" y1="0%" x2="100%" y2="100%">
<stop offset="0%" style="stop-color:${colors[0]};stop-opacity:1" />
<stop offset="100%" style="stop-color:${colors[1]};stop-opacity:1" />
</linearGradient>
</defs>
<rect x="0" y="0" width="100" height="100" rx="8" fill="url(#grad-company-${identifier})"/>
<text x="50" y="50" font-family="Arial, sans-serif" font-size="32" font-weight="600" fill="white" text-anchor="middle" dominant-baseline="central">${initials}</text>
</svg>`;
  
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * Avatar component with default fallback
 */
interface AvatarProps {
  src?: string | null;
  name?: string;
  id?: number;
  className?: string;
  alt?: string;
}

export const Avatar: React.FC<AvatarProps> = ({ src, name, id, className = 'w-8 h-8 rounded-full', alt }) => {
  const defaultSrc = getDefaultAvatar(name, id);
  const avatarSrc = src || defaultSrc;
  
  return (
    <img 
      src={avatarSrc} 
      className={className}
      alt={alt || name || 'User'} 
      onError={(e) => {
        // Fallback to default if image fails to load
        const target = e.target as HTMLImageElement;
        if (target.src !== defaultSrc) {
          target.src = defaultSrc;
        }
      }}
    />
  );
};

/**
 * Company Logo component with default fallback
 */
interface CompanyLogoProps {
  src?: string | null;
  name?: string;
  id?: number;
  className?: string;
  alt?: string;
}

export const CompanyLogo: React.FC<CompanyLogoProps> = ({ src, name, id, className = 'w-10 h-10 rounded-lg', alt }) => {
  const defaultSrc = getDefaultCompanyLogo(name, id);
  const logoSrc = src || defaultSrc;
  
  return (
    <img 
      src={logoSrc} 
      className={className}
      alt={alt || name || 'Company'} 
      onError={(e) => {
        // Fallback to default if image fails to load
        const target = e.target as HTMLImageElement;
        if (target.src !== defaultSrc) {
          target.src = defaultSrc;
        }
      }}
    />
  );
};

